import React, {memo, useEffect} from "react";
import {useDispatch, useSelector} from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme as useThemeMui} from '@mui/material';
import {AdminFooter, AdminNavbar, AdminSidebar} from '../components';
import {SET_MENU} from 'admin/store/actions';
import {AdminKeys, ConfigConstants} from "../config/AdminConfig";
import {useConfig} from "../context/ConfigProvider";
import {AnimationType, FramerAnimator} from "../../common/fusion-ui/animation/FramerAnimator";
import {checkNullStr, getJsonValue, isBoolTrue} from "../../common/fusion-ui/utils/ReactUtils";
import PropTypes from "prop-types";
import useScreenSize from "../../common/fusion-ui/hook/useScreenSize";
import {Section} from "../../common/fusion-ui";
import {useTheme} from "../../common/ProjectThemeProvider";

/**
 * AdminInterface is a React memo component designed for the admin section.
 * It provides a structure that includes an optional navbar, sidebar, and footer.
 *
 * Props:
 * - moduleName: The name of the current module.
 * - tag: A tag associated with the module.
 * - children: The content to be displayed within the main content section.
 * - animate: A boolean determining if the content should animate (default: true).
 * - showNavbar: A boolean to toggle the visibility of the navbar (default: true).
 * - showSidebar: A boolean to toggle the visibility of the sidebar (default: true).
 * - wrapContent: A boolean indicating if content should be wrapped in a Section (default: true).
 * - moduleAsHeading: Boolean to use module name as heading (default: true).
 * - showFooter: A boolean determining if the footer should be visible (default: true).
 * - heading: Custom heading text, overrides module name if provided.
 * - contentProps: Additional properties for the content section.
 *
 * This component uses several hooks and utilities:
 * - useThemeMui: For accessing the Material-UI theme.
 * - useTheme: For accessing the main theme.
 * - useScreenSize: Custom hook to determine screen size breakpoints.
 * - useConfig: Custom hook for accessing and updating app configurations.
 * - useMediaQuery: To manage media query breakpoints.
 * - useSelector: Redux hook to access the state.
 * - useDispatch: Redux hook to dispatch actions.
 *
 * Side Effects:
 * - The module and tag values are tracked using useEffect.
 *
 * Animation:
 * - Uses FramerAnimator for animating content appearance.
 */
const AdminInterface = memo(({
                                 moduleName,
                                 tag,
                                 children,
                                 animate = true,
                                 showNavbar = true,
                                 showSidebar = true,
                                 wrapContent = true,
                                 moduleAsHeading = true,
                                 showFooter = true,
                                 heading,
                                 contentProps = {},
                             }) => {
    const themeMui = useThemeMui();
    const theme = useTheme();
    const {isScreenUpMd, isScreenBeyondSm, isScreenBeyondMd, isScreenBeyondLg, screenWidth, checkScreenWidthBounds} = useScreenSize();
    const {getConfig, isConfigTrue, updateConfig} = useConfig();


    const matchDownMd = useMediaQuery(themeMui.breakpoints.down('md'));
    const isDrawerOpened = useSelector((state) => state.customization.opened);
    const dispatch = useDispatch();
    const animator = new FramerAnimator();


    const isDrawerResp = isDrawerOpened && !isScreenUpMd;
    const width = '100%';

    useEffect(() => {
        updateConfig(AdminKeys.tracker_currPageModule, moduleName);
        updateConfig(AdminKeys.tracker_currPageTag, tag);
        updateConfig(AdminKeys.tracker_SidebarToggleOn, isDrawerOpened);
    }, [moduleName]);

    const handleDrawerToggle = () => {
        const opened = !isDrawerOpened;
        dispatch({type: SET_MENU, opened: opened});
        updateConfig(AdminKeys.tracker_SidebarToggleOn, opened);
    };

    return (<div className={'d-flex'}>
        {/*navbar*/}
        {isBoolTrue(showNavbar) && <AdminNavbar onSidebarToggle={handleDrawerToggle}/>}

        {/* sidebar-drawer */}
        {isBoolTrue(showSidebar) && <AdminSidebar drawerOpen={!matchDownMd ? isDrawerOpened : !isDrawerOpened}
                                                  onSidebarToggle={handleDrawerToggle}
                                                  onMenuItemClick={handleDrawerToggle}/>}

        {/* content */}
        <div className={'container-fluid h-100 jc-center mt-6 py-2 scrollerx scroller-x-auto'}
             style={{
                 width,
                 maxWidth: width,
                 // width: '75%',
                 // maxWidth: '90%',
                 // maxWidth: isDrawerResp ? `calc(100%-500px)` : '100%',
                 marginLeft: checkScreenWidthBounds(724, 948) ? 0 : (isScreenBeyondMd ? (!isDrawerResp ? -ConfigConstants.drawerWidth : 0) : 0),
                 // marginLeft: (screenWidth >= 724 && screenWidth <= 948) ? 0 : (isScreenBeyondMd ? (!isDrawerResp ? -ConfigConstants.drawerWidth : 0) : 0),
                 // marginLeft: isScreenBeyondMd ? (shrinkContent ? -ConfigConstants.drawerWidth : 0) : 0,
                 // marginLeft: isScreenBeyondMd ? (isDrawerResp ? 0 : -ConfigConstants.drawerWidth) : 0,
                 // marginLeft: matchDownMd ? (isDrawerResp ? 0 : -ConfigConstants.drawerWidth) : 0,
                 // marginLeft: isScreenBeyondMd ? (isDrawerResp ? 0 : 0) : 0,
                 transition: "width .2s ease, padding .2s ease, margin .2s ease",
             }}>
            {/*<div>*/}
            {/*    <div>width: {width}</div>*/}
            {/*    <div>isDrawerResp: {isDrawerResp + ''}</div>*/}
            {/*    <div>isDrawerOpened: {isDrawerOpened + ''}</div>*/}
            {/*    <div>screenWidth: {screenWidth}</div>*/}
            {/*    <div>screenWidth-for-fixed: {checkScreenWidthBounds(724, 948) + ''}</div>*/}
            {/*</div>*/}
            {renderContent()}
            {isBoolTrue(showFooter) && <div className={'w-100 mt-2 mb-n2 ms-n3 me-n3'}>
                <div className={'position-static'}
                     style={{width: '110%', right: '0', bottom: '0',}}>
                    <AdminFooter position={''}/>
                </div>
            </div>}
        </div>
        {/*{!IsProd && <UICustomizationView/>}*/}
    </div>);

    function renderContent() {
        return <div className={`bg-warningx ${isScreenBeyondLg ? 'containerx' : ''}`}>
            {wrapContent ? <Section
                {...contentProps}
                className={`app-section px-3 py-3 height85 ${getJsonValue(contentProps, 'className')}`}
                borderless={isBoolTrue(getJsonValue(contentProps, 'borderless', true))}
                shadowVariant={getJsonValue(contentProps, 'shadowVariant', 'xs')}
                roundedVariant={getJsonValue(contentProps, 'roundedVariant', 4)}
                // title={(isBoolTrue(moduleAsHeading) && !checkNullStr(heading)) ? moduleName : heading}
                // headerClassName={`mt-n2 mb-2 ${getJsonValue(contentProps, 'headerClassName')}`}
                // titleClassName={`fw-bold fs-lg ${getJsonValue(contentProps, 'headingClassName')}`}
                // titleAt={getJsonValue(contentProps, 'headingAt', 'start')}
                // titleColor={getJsonValue(contentProps, 'headingColor', theme.primary)}
            >
                <div className={`mt-n1 mb-2 ${getJsonValue(contentProps, 'headerClassName')}`}>
                    {animator.setAnimationType(AnimationType.FADE_IN).setDelay(100)
                        .render(<label
                            className={`w-100 fw-bold fs-lg text-${getJsonValue(contentProps, 'headingAt', 'start')} ${getJsonValue(contentProps, 'headingClassName')}`}
                            style={{
                                color: getJsonValue(contentProps, 'headingColor', theme.primary),
                            }}>
                            {(isBoolTrue(moduleAsHeading) && !checkNullStr(heading)) ? moduleName : heading}
                        </label>)}
                </div>
                {renderChildren()}
            </Section> : renderChildren()}</div>
    }

    function renderChildren() {
        return isBoolTrue(animate) ? animator.setAnimationType(AnimationType.SLIDE_UP).setDelay(0)
            .render(<div>{children}</div>) : <div>{children}</div>
    }
});
AdminInterface.propTypes = {
    moduleName: PropTypes.string.isRequired,
    tag: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    animate: PropTypes.bool,
    showNavbar: PropTypes.bool,
    showSidebar: PropTypes.bool,
}

export default AdminInterface;
